import { z } from 'zod';
import { amountSchema, currencyCodeSchema, dateOnlySchema, discountSchema, exchangeRateSchema, exchangeRateSourceSchema, quantitySchema } from './common';
import { tcsChargeSchema } from './withholding';
import { uqcSchema } from './gst';
/**
 * The shapes shared by every money document - invoices, estimates, credit
 * notes, bills, purchase orders.
 *
 * They differ in which fields they require and what their statuses mean, but a
 * line item is a line item. One schema here means the line editor, the tax
 * engine and the PDF renderer all speak the same language.
 */
export const DOCUMENT_TYPES = [
    'invoice',
    'proforma_invoice',
    'estimate',
    'recurring_invoice',
    'credit_note',
    'retainer_invoice',
    'bill',
    'purchase_order',
    'vendor_credit',
];
/**
 * The choke point this vocabulary never had.
 *
 * `documents.doc_type` is a free `varchar(30)` and every writer passes a string
 * literal, so until now nothing checked that a value was one of the eight above
 * - the list was documentation with no teeth. Anything that takes a document
 * type across a boundary parses it through here.
 */
export const documentTypeSchema = z.enum(DOCUMENT_TYPES);
/**
 * Types that are a demand for money but NOT a tax invoice.
 *
 * A proforma shows the tax a buyer will pay without creating the liability: no
 * supply has happened, so it reaches no return, is owed by nobody, and cannot
 * be paid. It converts into a real invoice when the customer agrees.
 *
 * Kept as a named list rather than an `=== 'proforma_invoice'` check because
 * estimates will join it, and a list is the thing a future reader greps for.
 */
export const NON_TAX_DOCUMENT_TYPES = ['proforma_invoice', 'estimate'];
export const isTaxDocument = (docType) => !NON_TAX_DOCUMENT_TYPES.includes(docType);
/**
 * The lifecycle a HUMAN chose. Everything else about a document's state is
 * derived - see `deriveInvoiceStatus`.
 *
 * `draft`      not issued; freely editable, no number reserved on the customer's side
 * `sent`       issued to the customer; edits from here restate a seen figure
 * `void`       cancelled without money moving. The number is retained, because
 *              a gap in an invoice sequence is a question from an auditor.
 * `written_off` given up on. Distinct from void: the sale happened, the money
 *              did not arrive, and the two are reported differently.
 */
export const DOCUMENT_LIFECYCLES = ['draft', 'sent', 'void', 'written_off'];
/**
 * What the UI shows. `paid`, `partially_paid` and `overdue` are NEVER stored -
 * they are functions of the balance and the due date, and a stored `overdue`
 * flag is wrong every night between midnight and whenever the cron runs.
 */
export const DOCUMENT_STATUSES = [
    'draft',
    'sent',
    'partially_paid',
    'paid',
    'overdue',
    'void',
    'written_off',
];
/**
 * Derive what to show from what is stored.
 *
 * `balanceDueMinor` and `totalMinor` are integer minor units, so the
 * comparisons are exact - `balance <= 0` on a float would call a 1-paise
 * rounding residue an unpaid invoice forever.
 *
 * `asOf` is passed in rather than read from the clock so the caller controls
 * the timezone. "Overdue" is a question about the customer's calendar day, and
 * a server in UTC deciding it flips an Indian invoice to overdue five and a half
 * hours early.
 */
export function deriveDocumentStatus(input) {
    const { lifecycle, totalMinor, balanceDueMinor, dueDate, asOf } = input;
    // A human's decision always wins over anything derived.
    if (lifecycle === 'draft' || lifecycle === 'void' || lifecycle === 'written_off')
        return lifecycle;
    // Settled. `<= 0` rather than `=== 0` so an overpayment reads as paid rather
    // than falling through to overdue.
    if (balanceDueMinor <= 0)
        return 'paid';
    const partiallyPaid = balanceDueMinor < totalMinor;
    // Overdue outranks partially-paid: someone chasing money needs to see the
    // date has passed, and that it is part-paid is the detail underneath.
    if (dueDate && dueDate < asOf)
        return 'overdue';
    return partiallyPaid ? 'partially_paid' : 'sent';
}
/** Whether a document may still be edited without the `manage` permission. */
export function isFreelyEditable(lifecycle) {
    return lifecycle === 'draft';
}
// ── Line items ────────────────────────────────────────────────────────────
export const documentLineSchema = z
    .object({
    /** Client-generated, so a line survives reordering before the first save. */
    id: z.string().trim().min(1).max(64),
    /** Null for a free-text line - the item catalogue is a convenience, not a requirement. */
    itemId: z.string().trim().max(36).nullable().optional(),
    name: z.string().trim().min(1, 'Every line needs a description').max(300),
    description: z.string().trim().max(2000).optional(),
    quantity: quantitySchema.refine((q) => q !== 0, { message: 'Quantity cannot be zero' }),
    rate: amountSchema,
    unit: z.string().trim().max(30).optional(),
    /** Snapshotted onto the line for Table 12. Inferred from `unit` if absent. */
    uqc: uqcSchema.optional(),
    discount: discountSchema.optional(),
    taxId: z.string().trim().max(36).nullable().optional(),
    /** Goods and services classification. Required on GST documents. */
    hsnSac: z.string().trim().max(20).optional(),
    /**
     * What KIND of revenue this line is, overriding the document's own.
     *
     * The reason the classification is snapshotted per line rather than per
     * document is that one invoice routinely mixes revenue streams -
     * "development 60,000, video 20,000" is one invoice and two rows in the
     * breakdown. That was designed, indexed and documented, and then reachable
     * by no client at all, which made the whole rationale untrue. This is the
     * field that makes it expressible.
     *
     * Optional, and almost always absent: the composer sets one value on the
     * document and the server stamps it onto every line that carries none. See
     * `stampIncomeCategoryOntoLines`.
     */
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
    /** Position in the document. Rewritten server-side to be dense. */
    sortOrder: z.number().int().min(0).optional(),
})
    .strict();
/** Fields every document header carries, whichever type it is. */
export const documentBaseSchema = z.object({
    date: dateOnlySchema,
    /** Free-text reference the customer gave you - a PO number, a contract id. */
    referenceNumber: z.string().trim().max(120).optional(),
    /** Absent means the customer's currency, else the book's base. */
    currency: currencyCodeSchema.optional(),
    /**
     * Base-currency units per ONE unit of the document currency - rupees per
     * pound, 127.5000 - fixed at the moment the document is issued. Required
     * when the currency is not the base; ignored when it is. Always stored,
     * never looked up later: a rate is a fact about a moment, and re-fetching
     * it would restate history.
     */
    exchangeRate: exchangeRateSchema.optional(),
    exchangeRateSource: exchangeRateSourceSchema.optional(),
    /**
     * The day a published rate is FOR - the feed answers with the latest
     * business day on or before the date asked, so it can be a Friday for a
     * Sunday invoice. Printed beside the rate; meaningless for a typed rate.
     */
    exchangeRateDate: dateOnlySchema.optional(),
    lines: z.array(documentLineSchema).min(1, 'Add at least one line'),
    discount: discountSchema.optional(),
    shippingCharge: amountSchema.optional(),
    shippingTaxId: z.string().trim().max(36).nullable().optional(),
    /** Round-off. May be negative. */
    adjustment: amountSchema.optional(),
    adjustmentLabel: z.string().trim().max(60).optional(),
    /**
     * TCS collected on top of this document.
     *
     * The seller computes this one, unlike TDS - so only the section and rate
     * are sent, and the amount is derived on the server from the total including
     * GST. See `tcsBaseFor` for why that base differs from the TDS base.
     */
    tcs: tcsChargeSchema.optional(),
    taxMode: z.enum(['exclusive', 'inclusive']).optional(),
    /** Where supply is made TO. Drives the GST split. */
    placeOfSupply: z.string().trim().toUpperCase().max(4).optional(),
    reverseCharge: z.boolean().optional(),
    terms: z.string().trim().max(4000).optional(),
    notes: z.string().trim().max(4000).optional(),
    salespersonId: z.string().trim().max(36).nullable().optional(),
    /**
     * Which job this document is for. At most one, which is what makes the
     * project report a real P&L rather than an overlapping list.
     *
     * Consequence a caller has to accept: an invoice that mixes two projects'
     * work has to be raised as two invoices. That is the right constraint - an
     * invoice is what the customer received, and "half of INV-00042 was Project
     * A" is not a fact any customer, auditor or portal recognises.
     */
    projectId: z.string().trim().max(36).nullable().optional(),
    /**
     * What KIND of revenue this is. Stamped onto every line at save time, which
     * is where reports read it from - see `documents.incomeCategoryId` in
     * schema.ts for why the snapshot is on the line and not on the header.
     */
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
    /** The labels on this document. Replaced wholesale; up to ten. */
    tagIds: z
        .array(z.string().trim().min(1).max(36))
        .max(10, 'You can put ten tags on a record')
        .transform((ids) => Array.from(new Set(ids)))
        .optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=documents.js.map